import { Component, ElementRef, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MessageLogFrontService } from '../../../services/message-log/message-log.service';
import { PushDevicesDialogComponent } from '../push-devices-dialog/push-devices-dialog.component';
import { NotificacionesService } from '../../../services/notificaciones/notificacionesService';

@Component({
    selector: 'app-push-history',
    templateUrl: './push-history.component.html',
    styleUrls: ['./push-history.component.scss'],
})
export class PushHistoryComponent implements OnInit {
    @ViewChild('logsSection') logsSection!: ElementRef;

    loading = false;
    loadingText = 'Procesando…';

    totalDevices = 0;
    totalDevicesIOS = 0;
    totalDevicesAndroid = 0;

    totalPush = 0;
    totalIOS = 0;
    totalAndroid = 0;
    usuariosInvolucrados = 0;

    userQuery = '';
    userResults: any[] = [];
    selectedUser: any = null;

    logs: any[] = [];
    notificacionesList: any[] = [];
    showDocNotifications = false;

    get filteredNotificaciones(): any[] {
        if (this.showDocNotifications) {
            return this.notificacionesList;
        }
        return this.notificacionesList.filter(n => {
            const title = (n.asunto || '').toLowerCase();
            return !title.includes('documento') && !title.includes('documentación');
        });
    }
    selectedNotificationDetail: any = null;
    @ViewChild('recipientsModal') recipientsModal!: TemplateRef<any>;
    destinatariosDetalle: any[] = [];
    loadingDestinatarios = false;
    displayedColumns = ['fecha', 'device', 'titulo', 'mensaje'];

    desde = '';
    hasta = '';

    chartData: any;
    chartLabels: string[] = [];

    chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
    };

    logFilter = '';

    constructor(
        private logService: MessageLogFrontService,
        private notificacionesService: NotificacionesService,
        private dialog: MatDialog,
    ) {}

    async ngOnInit() {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        this.desde = this.toISODate(firstDay);
        this.hasta = this.toISODate(lastDay);

        await this.reloadAll();
    }

    openDevicesModal() {
        this.dialog.open(PushDevicesDialogComponent, {
            width: '900px',
            maxHeight: '80vh',
        });
    }

    async loadDevices(useOverlay = true) {
        const exec = async () => {
            const r = await this.logService.getDevicesSummary();
            const data = r?.response?.response || {};

            this.totalDevices = data.totalDevices || 0;
            this.totalDevicesIOS = data.totalIOS || 0;
            this.totalDevicesAndroid = data.totalAndroid || 0;
        };

        if (!useOverlay) return exec();
        return this.runLoading('Contando dispositivos activos…', exec);
    }

    private toISODate(d: Date) {
        return d.toISOString().slice(0, 10);
    }

    private async runLoading<T>(
        text: string,
        fn: () => Promise<T>,
    ): Promise<T> {
        this.loading = true;
        this.loadingText = text;
        try {
            return await fn();
        } finally {
            this.loading = false;
            this.loadingText = '';
        }
    }

    async reloadAll() {
        await this.runLoading('Cargando métricas…', async () => {
            await this.loadSummary(false);
            await this.loadDevices(false);
            await this.loadChart(false);

            try {
                const hist = await this.notificacionesService.getHistorial(new Date(this.desde), new Date(this.hasta));
                this.notificacionesList = hist || [];
            } catch (e) {
                console.error('Error fetching custom notifications history:', e);
            }

            if (this.selectedUser?._id) {
                await this.openUserLogs(this.selectedUser, false);
            }
        });
    }

    async selectNotification(notif: any) {
        await this.runLoading('Cargando detalle de métricas…', async () => {
            try {
                this.selectedNotificationDetail = await this.notificacionesService.getDetalleMetricas(notif._id);
            } catch (e) {
                console.error('Error fetching metrics detail:', e);
            }
        });
    }

    closeNotificationDetail() {
        this.selectedNotificationDetail = null;
    }

    async loadSummary(useOverlay = true) {
        const exec = async () => {
            const r = await this.logService.getPushSummary(
                this.desde,
                this.hasta,
            );

            const data = r?.response || {};

            this.totalPush = data.totalPush || 0;
            this.totalIOS = data.totalIOS || 0;
            this.totalAndroid = data.totalAndroid || 0;
            this.usuariosInvolucrados = data.usuariosInvolucrados || 0;
        };

        if (!useOverlay) return exec();
        return this.runLoading('Calculando indicadores…', exec);
    }

    async onSearchUsers() {
        if (!this.userQuery || this.userQuery.trim().length < 2) {
            this.userResults = [];
            return;
        }

        const res = await this.logService.searchUsers(
            this.userQuery.trim(),
            12,
            this.desde,
            this.hasta,
        );

        this.userResults = (res as any)?.response || [];
    }

    async openUserLogs(u: any, useOverlay = true) {
        this.selectedUser = u;

        const exec = async () => {
            const res = await this.logService.getPushByUser(
                u._id,
                this.desde,
                this.hasta,
            );

            this.logs = (res as any)?.response || [];

            setTimeout(() => {
                this.logsSection?.nativeElement?.scrollIntoView({
                    behavior: 'smooth',
                });
            }, 100);
        };

        if (!useOverlay) return exec();

        return this.runLoading(
            `Cargando mensajes de ${u?.nombrePersona || 'usuario'}…`,
            exec,
        );
    }

    clearUser() {
        this.selectedUser = null;
        this.logs = [];
    }

    async loadChart(useOverlay = true) {
        const exec = async () => {
            const res = await this.logService.getDailyRange(
                this.desde,
                this.hasta,
            );

            const stats = res?.response || [];

            this.chartData = {
                labels: stats.map((s: any) => s._id),
                datasets: [
                    {
                        label: 'Total Push',
                        data: stats.map((s: any) => s.total),
                        tension: 0.4,
                        fill: false,
                    },
                    {
                        label: 'iOS',
                        data: stats.map((s: any) => s.ios),
                        tension: 0.4,
                        fill: false,
                    },
                    {
                        label: 'Android',
                        data: stats.map((s: any) => s.android),
                        tension: 0.4,
                        fill: false,
                    },
                ],
            };
        };

        if (!useOverlay) return exec();
        return this.runLoading('Generando gráfico…', exec);
    }

    async applyRange() {
        if (!this.desde || !this.hasta) return;
        await this.reloadAll();
    }

    get filteredLogs() {
        if (!this.logFilter || this.logFilter.trim().length < 2) {
            return this.logs;
        }

        const q = this.logFilter.toLowerCase();

        return this.logs.filter(
            (l) =>
                (l.titulo || '').toLowerCase().includes(q) ||
                (l.mensaje || '').toLowerCase().includes(q),
        );
    }
}
